'use client';

import React, { useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import { CurrencyIcon } from '@/blocks/components/AssetIcon';
import { SearchStartAdornment } from '@/blocks/components/Controls/Autocomplete/SearchStartAdornment';
import { Menu, SelectItem, SelectItemsList, useMenu } from '@/blocks/components/Controls/Menu';
import { TextField } from '@/blocks/components/Controls/TextField';
import { ChevronDown } from '@/blocks/components/Icons/Chevron';
import { useIsRtl } from '@/hooks/useIsRtl';
import typography from '@/styles/typography.module.scss';

import styles from './styles.module.scss';

type CurrencySelectProps = {
  isFiat?: boolean;
  options: string[];
  value: string;
  onValueChange(value: string): void;
};

export function CurrencySelect({
  isFiat = false,
  options,
  value,
  onValueChange,
  ...rest
}: CurrencySelectProps) {
  const { isRtl } = useIsRtl();
  const tr = useTranslations();
  const [searchOption, setSearchOption] = useState('');
  const { anchorEl, open, handleOpen, handleClose } = useMenu({
    onClose() {
      setSearchOption('');
    },
  });

  const filteredOptions = useMemo(
    () => options.filter((option) => option.toLowerCase().includes(searchOption.toLowerCase())),
    [searchOption, options],
  );

  const handleMenuItemClick = (event: React.MouseEvent<HTMLElement>, value: string) => {
    onValueChange(value);
    handleClose();
  };

  return (
    <Menu
      className={styles.selectMenu}
      anchorEl={anchorEl}
      open={open}
      handleClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: isRtl ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: isRtl ? 'left' : 'right',
      }}
      renderButton={(buttonId, menuId) => (
        <button
          id={buttonId}
          className={classNames(styles.selectButton, { [styles.active]: open })}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpen}
        >
          <div className={styles.selectIcon}>
            <CurrencyIcon currencyName={value} width={18} height={18} />
          </div>
          {value}
          <span className={classNames(styles.chevronDown, { [styles.active]: open })}>
            <ChevronDown />
          </span>
        </button>
      )}
      {...rest}
    >
      {options?.length > 8 ? (
        <div
          className={styles.selectSearchWrapper}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
        >
          <TextField
            placeholder={tr('pages.buyCrypto.converterWidget.search.label')}
            type={'search'}
            fullWidth
            InputProps={{
              startAdornment: <SearchStartAdornment />,
            }}
            value={searchOption}
            onChange={(e) => setSearchOption(e.target.value)}
          />
        </div>
      ) : null}

      <SelectItemsList>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            return (
              <SelectItem
                key={option}
                selected={option === value}
                onClick={(e) => handleMenuItemClick(e, option)}
                icon={<CurrencyIcon currencyName={option} width={18} height={18} />}
              >
                {option}
              </SelectItem>
            );
          })
        ) : (
          <div className={classNames(typography.bodyM, styles.emptyResult)}>
            {tr.rich('pages.buyCrypto.converterWidget.search.emptyResult')}
          </div>
        )}
      </SelectItemsList>
    </Menu>
  );
}
